import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  FileText,
  Users,
  Send,
  ArrowLeft,
  X,
  Search,
  Loader2,
  CheckCircle2,
  LayoutTemplate,
  Building2,
  Globe,
  UserPlus,
  Mail,
  Shield
} from "lucide-react";
import { toast } from "sonner";

export default function NewSignaturePage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recipientTab, setRecipientTab] = useState("employees");
  const [showExternalDialog, setShowExternalDialog] = useState(false);
  const [externalForm, setExternalForm] = useState({ email: "", full_name: "" });

  const [formData, setFormData] = useState({
    documentId: "",
    recipients: [],
    message: "",
    templateId: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, employeesRes, templatesRes] = await Promise.all([
        api.get("/documents"),
        api.get("/employees"),
        api.get("/templates")
      ]);
      setDocuments(docsRes.data);
      setEmployees(employeesRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const searchRecipients = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await api.get(`/recipients/search?q=${encodeURIComponent(query)}`);
      // Filter out already selected
      const filtered = response.data.filter(
        (r) => !formData.recipients.find((selected) => selected.email === r.email)
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error("Search error:", error);
      // Fallback to local employee filter
      const filtered = employees.filter(
        (emp) =>
          (emp.full_name?.toLowerCase().includes(query.toLowerCase()) ||
            emp.email?.toLowerCase().includes(query.toLowerCase())) &&
          !formData.recipients.find((r) => r.email === emp.email)
      );
      setSearchResults(filtered);
    } finally {
      setSearching(false);
    }
  };

  const addRecipient = (recipient) => {
    const newRecipient = {
      id: recipient.id || `external-${Date.now()}`,
      email: recipient.email,
      full_name: recipient.full_name,
      type: recipient.type || "internal",
      department: recipient.department
    };
    setFormData({
      ...formData,
      recipients: [...formData.recipients, newRecipient]
    });
    setSearchQuery("");
    setSearchResults([]);
  };

  const addExternalRecipient = () => {
    if (!externalForm.email || !externalForm.full_name) {
      toast.error("Email et nom sont obligatoires");
      return;
    }
    // Simple email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(externalForm.email)) {
      toast.error("Email invalide");
      return;
    }
    // Check if already added
    if (formData.recipients.find((r) => r.email === externalForm.email)) {
      toast.error("Ce destinataire a déjà été ajouté");
      return;
    }
    
    addRecipient({
      id: `external-${Date.now()}`,
      email: externalForm.email,
      full_name: externalForm.full_name,
      type: "external"
    });
    setExternalForm({ email: "", full_name: "" });
    setShowExternalDialog(false);
    toast.success("Signataire externe ajouté");
  };

  const removeRecipient = (email) => {
    setFormData({
      ...formData,
      recipients: formData.recipients.filter((r) => r.email !== email)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.documentId) {
      toast.error("Veuillez sélectionner un document");
      return;
    }
    if (formData.recipients.length === 0) {
      toast.error("Veuillez ajouter au moins un destinataire");
      return;
    }

    setSubmitting(true);
    try {
      // Use template fields if selected, otherwise create default fields
      let fields;
      if (formData.templateId) {
        const template = templates.find((t) => t.id === formData.templateId);
        fields = template?.fields || [];
        // Assign recipients to signature fields
        fields = fields.map((field, idx) => ({
          ...field,
          recipient_email: formData.recipients[idx % formData.recipients.length]?.email
        }));
      } else {
        // Create simple signature fields for each recipient
        fields = formData.recipients.map((recipient, index) => ({
          id: `field-${index}`,
          type: "signature",
          recipient_email: recipient.email,
          page: 1,
          x: 100,
          y: 100 + index * 100,
          width: 200,
          height: 50
        }));
      }

      // Format recipients for API - include type for external signers
      const recipientsData = formData.recipients.map((r) => ({
        email: r.email,
        full_name: r.full_name,
        type: r.type || "internal"
      }));

      await api.post("/signatures/request", {
        document_id: formData.documentId,
        recipients: recipientsData,
        message: formData.message || null,
        fields
      });

      const hasExternal = formData.recipients.some((r) => r.type === "external");
      toast.success(
        hasExternal
          ? "Demande envoyée ! Les signataires externes recevront un lien sécurisé."
          : "Demande de signature envoyée"
      );
      navigate("/signatures");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Erreur lors de l'envoi de la demande"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDocument = documents.find((d) => d.id === formData.documentId);
  const filteredEmployees = employees.filter(
    (emp) =>
      (emp.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.department?.toLowerCase().includes(searchQuery.toLowerCase())) &&
      !formData.recipients.find((r) => r.email === emp.email)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 max-w-3xl mx-auto" data-testid="new-signature-page">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 mt-1"
          onClick={() => navigate("/signatures")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">
            Nouvelle demande de signature
          </h1>
          <p className="text-xs sm:text-base text-muted-foreground mt-1">
            Envoyez un document à signer
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Template Selection */}
        {templates.length > 0 && (
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <LayoutTemplate className="w-4 h-4 sm:w-5 sm:h-5" />
                Template (optionnel)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              <Select
                value={formData.templateId || "none"}
                onValueChange={(value) =>
                  setFormData({ ...formData, templateId: value === "none" ? "" : value })
                }
              >
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="Aucun template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun template</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.fields?.length || 0} champs)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Document Selection */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              Document à signer
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <Select
              value={formData.documentId}
              onValueChange={(value) =>
                setFormData({ ...formData, documentId: value })
              }
            >
              <SelectTrigger data-testid="document-select">
                <SelectValue placeholder="Sélectionnez un document" />
              </SelectTrigger>
              <SelectContent>
                {documents.map((doc) => (
                  <SelectItem key={doc.id} value={doc.id}>
                    {doc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDocument && (
              <div className="mt-3 p-3 rounded-lg bg-muted flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate text-sm">{selectedDocument.name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="text-xs">
                      {selectedDocument.category}
                    </Badge>
                    <span>
                      {(selectedDocument.file_size / 1024).toFixed(1)} Ko
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recipients Selection */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              Destinataires
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
            {/* Selected Recipients */}
            {formData.recipients.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.recipients.map((recipient) => (
                  <Badge
                    key={recipient.email}
                    variant={recipient.type === "external" ? "outline" : "secondary"}
                    className="py-1.5 px-3 flex items-center gap-2"
                  >
                    {recipient.type === "external" ? (
                      <Globe className="w-3 h-3 text-orange-500" />
                    ) : (
                      <Building2 className="w-3 h-3 text-blue-500" />
                    )}
                    <span className="text-sm">{recipient.full_name}</span>
                    <button
                      type="button"
                      onClick={() => removeRecipient(recipient.email)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Recipient Selection Tabs */}
            <Tabs value={recipientTab} onValueChange={setRecipientTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="employees" className="text-xs sm:text-sm">
                  <Building2 className="w-4 h-4 mr-1 sm:mr-2" />
                  Employés AAEA
                </TabsTrigger>
                <TabsTrigger value="external" className="text-xs sm:text-sm">
                  <Globe className="w-4 h-4 mr-1 sm:mr-2" />
                  Externe
                </TabsTrigger>
              </TabsList>

              {/* Employees Tab */}
              <TabsContent value="employees" className="mt-4 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher un employé..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchRecipients(e.target.value);
                    }}
                    className="pl-10"
                    data-testid="employee-search"
                  />
                </div>

                {/* Employee List */}
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {searching ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : searchQuery.length >= 2 && searchResults.length > 0 ? (
                    searchResults.map((result) => (
                      <div
                        key={result.email}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted cursor-pointer"
                        onClick={() => addRecipient(result)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-medium text-primary">
                              {result.full_name?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{result.full_name}</p>
                            <p className="text-xs text-muted-foreground">{result.email}</p>
                          </div>
                        </div>
                        {result.department && (
                          <Badge variant="outline" className="text-xs">
                            {result.department}
                          </Badge>
                        )}
                      </div>
                    ))
                  ) : searchQuery.length >= 2 && filteredEmployees.length > 0 ? (
                    filteredEmployees.slice(0, 5).map((emp) => (
                      <div
                        key={emp.email}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted cursor-pointer"
                        onClick={() => addRecipient({ ...emp, type: "internal" })}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-medium text-primary">
                              {emp.full_name?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{emp.full_name}</p>
                            <p className="text-xs text-muted-foreground">{emp.email}</p>
                          </div>
                        </div>
                        {emp.department && (
                          <Badge variant="outline" className="text-xs">
                            {emp.department}
                          </Badge>
                        )}
                      </div>
                    ))
                  ) : searchQuery.length < 2 ? (
                    <p className="text-center text-sm text-muted-foreground py-4">
                      Tapez au moins 2 caractères pour rechercher
                    </p>
                  ) : (
                    <p className="text-center text-sm text-muted-foreground py-4">
                      Aucun employé trouvé
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* External Tab */}
              <TabsContent value="external" className="mt-4 space-y-4">
                <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-orange-800 dark:text-orange-200">
                        Signataire externe
                      </p>
                      <p className="text-orange-700 dark:text-orange-300 mt-1">
                        Les personnes externes recevront un lien sécurisé par email. 
                        Elles devront vérifier leur identité par code OTP avant de signer.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowExternalDialog(true)}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter un signataire externe
                </Button>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Message */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg">Message (optionnel)</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <Textarea
              placeholder="Ajoutez un message pour les destinataires..."
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              rows={3}
              data-testid="message-input"
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/signatures")}
            className="w-full sm:w-auto"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={submitting || !formData.documentId || formData.recipients.length === 0}
            className="w-full sm:flex-1"
            data-testid="submit-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Envoyer la demande
          </Button>
        </div>
      </form>

      {/* External Signer Dialog */}
      <Dialog open={showExternalDialog} onOpenChange={setShowExternalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-orange-500" />
              Ajouter un signataire externe
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ext-name">Nom complet *</Label>
              <Input
                id="ext-name"
                placeholder="Jean Martin"
                value={externalForm.full_name}
                onChange={(e) =>
                  setExternalForm({ ...externalForm, full_name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ext-email">Email *</Label>
              <Input
                id="ext-email"
                type="email"
                placeholder="jean.martin@entreprise.fr"
                value={externalForm.email}
                onChange={(e) =>
                  setExternalForm({ ...externalForm, email: e.target.value })
                }
              />
            </div>
            <div className="p-3 rounded-lg bg-muted text-sm text-muted-foreground">
              <Mail className="w-4 h-4 inline mr-2" />
              Cette personne recevra un email avec un lien sécurisé pour signer le document.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExternalDialog(false)}>
              Annuler
            </Button>
            <Button onClick={addExternalRecipient}>
              <UserPlus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
